import type { _2dMapCombinedQuote } from '@larki/shared/type/order.v2';

import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useCommitQuoteMutation } from '@/redux/rtk';
import { applyRepricedQuote } from '@/redux/reducers/order.v2';
import { orderV2Selectors } from '@/redux/selectors/order.v2';
import { AppDispatch } from '@/utilities';
import { usePaymentContext } from './usePaymentContext';
import { useEffect } from 'react';

// Shared by both commit paths: sync the display to what the server persisted.
export const applyCommitReprice = (
  dispatch: AppDispatch,
  {
    jobId,
    asMember,
    repriced,
  }: {
    jobId: number;
    asMember: boolean;
    repriced?: _2dMapCombinedQuote;
  }
) => {
  if (!repriced) return;
  dispatch(applyRepricedQuote({ jobId, asMember, repriced }));
};

/**
 * Commits the quote if not already committed.
 * ONLY to be used inside of the PaymentContext.Provider.
 */
export const useCommitQuote = () => {
  const {
    jobId,
    asMember,
    payQuoteId,
    setCommittedQuoteId,
    committedQuoteId,
    lastCommittedMembershipPriceId,
    setLastCommittedMembershipPriceId,
  } = usePaymentContext();
  const dispatch = useAppDispatch();
  const quote = useAppSelector((state) =>
    orderV2Selectors.getQuote(state, jobId, asMember)
  );
  const quoteInputs = useAppSelector((state) =>
    orderV2Selectors.getQuoteInputs(state, jobId)
  );
  const membershipPriceId = useAppSelector(
    (state) => state.order.membershipPriceId
  );
  const [commitQuote, { status }] = useCommitQuoteMutation();

  useEffect(() => {
    // Pay Invoice mode pays an existing quote — never commit a new one here.
    if (payQuoteId) return;
    if (!quote) return;

    const membershipChanged =
      committedQuoteId !== null &&
      lastCommittedMembershipPriceId !== null &&
      lastCommittedMembershipPriceId !== membershipPriceId;

    if (!membershipChanged) {
      if (status !== 'uninitialized' || committedQuoteId) return;
    }

    commitQuote({
      jobId,
      data: {
        quote,
        quoteInputs: quoteInputs ? { ...quoteInputs, asMember } : undefined,
      },
    })
      .unwrap()
      .then(({ quoteId, quote: repriced }) => {
        applyCommitReprice(dispatch, { jobId, asMember, repriced });
        setCommittedQuoteId(quoteId);
        setLastCommittedMembershipPriceId(membershipPriceId);
      })
      .catch((err) => {
        console.error('commitQuote failed', err);
      });
  }, [
    jobId,
    asMember,
    payQuoteId,
    quote,
    quoteInputs,
    status,
    committedQuoteId,
    commitQuote,
    dispatch,
    setCommittedQuoteId,
    lastCommittedMembershipPriceId,
    setLastCommittedMembershipPriceId,
    membershipPriceId,
  ]);

  return committedQuoteId;
};
